import { CollapsibleContent } from '@ctw/shared/components/containers/collapsible-content';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import {
  ClinicalObjectCard,
  type ClinicalObjectCardProps,
} from '@ctw/shared/content/clinical-object/clinical-object-card';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import type { PropsWithChildren, ReactNode } from 'react';

export interface ClinicalObjectGroupItem
  extends Omit<ClinicalObjectCardProps, 'children' | 'grouped'> {
  id: string;
  content?: ReactNode;
}

export interface ClinicalObjectGroupProps extends PropsWithChildren {
  label: string;
  items: Readonly<Array<ClinicalObjectGroupItem>>;
  collapsible?: {
    disclosureLabel: string;
    initiallyDisclosed: boolean;
  };
  loading?: boolean;
  renderContent?: { footer: ReactNode };
  focalHeader?: boolean;
}

export const ClinicalObjectGroup = ({
  label,
  children,
  items,
  collapsible,
  loading = false,
  focalHeader = false,
  renderContent,
}: ClinicalObjectGroupProps) => {
  return (
    <section className={tw`flex flex-col overflow-visible`} aria-label={label}>
      <div
        className={twx(
          'flex h-full w-full flex-col rounded-md border border-border-main bg-white shadow-sm focus-visible:outline-4 focus-visible:outline-primary-main',
          {
            'gap-1.5': Boolean(children),
          },
        )}
      >
        <header
          className={twx('flex justify-between gap-2 px-3 py-2', {
            'border-content-border border-b bg-background-hover mix-blend-darken': focalHeader,
          })}
        >
          <div
            className={tw`flex max-h-min w-full flex-1 grow basis-1/3 flex-col items-start gap-1.5`}
          >
            {label && (
              <div
                className={tw`flex min-h-[22px] items-center font-medium text-content-subtle text-sm uppercase leading-none`}
              >
                <span>{label}</span>
              </div>
            )}
          </div>
        </header>
        {children && <div className={tw`px-3`}>{children}</div>}
        {loading && (
          <LoadingSpinner
            iconClass={tw`h-5 w-5`}
            className={tw`flex w-full items-center justify-center py-5`}
          />
        )}
        {!loading && items.length === 0 && (
          <div className={tw`flex items-center justify-center px-3 py-3 text-sm`}>
            <div className={tw`text-content-subtle`}>No {label.toLowerCase()} available.</div>
          </div>
        )}
        {!loading && items.length > 0 && (
          <CollapsibleContent
            initiallyOpen={collapsible?.initiallyDisclosed ?? true}
            label={`${collapsible?.disclosureLabel ?? 'Details'} (${items.length})`}
            summaryClassName={twx('hidden pl-3', {
              flex: collapsible,
            })}
          >
            <ul className={tw`flex flex-col divide-y divide-border-main`}>
              {items.map(({ id, content, ...cardProps }) => (
                <li
                  key={id}
                  className={twx('last:rounded-b-md', {
                    'first:!border-t': !focalHeader,
                  })}
                >
                  <ClinicalObjectCard grouped={true} {...cardProps}>
                    {content}
                  </ClinicalObjectCard>
                </li>
              ))}
            </ul>
          </CollapsibleContent>
        )}
      </div>
      {renderContent?.footer && (
        <footer className={tw`flex max-h-min items-end justify-between gap-1 p-3`}>
          {renderContent.footer}
        </footer>
      )}
    </section>
  );
};
